import { getFirebaseServices } from '@/config/firebase'; 
import { logEvent as firebaseLogEvent } from 'firebase/analytics';
import { useConsentStore } from '@/stores/consentStore'; 

interface FirebaseEventParams { [key: string]: any; }

export const logFirebaseEvent = async (eventName: string, params?: FirebaseEventParams): Promise<void> => {
  try {
    if (!import.meta.env.PROD || location.hostname !== 'runofftrack.com' ||
        sessionStorage.getItem('runofftrack.analytics_disabled') === '1' ||
        useConsentStore.getState().consent.analytics !== 'granted') return;
    const { analytics } = await getFirebaseServices();
    if (analytics && useConsentStore.getState().consent.analytics === 'granted') firebaseLogEvent(analytics, eventName, params);
  } catch (error) { console.error(`Marketing: Error logging Firebase Analytics event: ${eventName}`, error); }
};

declare global { 
  interface Window {
    fbq?: (...args: any[]) => void; 
    rdt?: { (action: string, eventNameOrPixelId: string, eventDataOrOptions?: object): void; sendEvent?: (...args: any[]) => void; callQueue?: any[]; };
    dataLayer?: any[]; gtag?: (...args: any[]) => void;
    ttq?: { (action: string, eventNameOrPixelId: string, eventDataOrOptions?: object): void; page: (...args: any[]) => void; track: (...args: any[]) => void; load: (pixelId: string) => void; instance: (pixelId: string) => any; _i?: any; methods?: string[]; setAndDefer?: (...args: any[]) => void; };
    $crisp?: any[];
    CRISP_WEBSITE_ID?: string;
  }
}

let isFbPixelInitialized = false;
let isRedditPixelInitialized = false;
let isTikTokPixelInitialized = false;
let isCrispChatInitialized = false;

// --- Initialization Logic ---
export const initFacebookPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isFbPixelInitialized || typeof window === 'undefined' || !window.fbq || consent.advertising !== 'granted') {
    return;
  }
  const pixelId = import.meta.env.VITE_FACEBOOK_PIXEL_ID;
  if (pixelId) {
    try { window.fbq('init', pixelId); window.fbq('track', 'PageView'); isFbPixelInitialized = true; }
    catch (error) { console.error('Marketing: Error initializing Facebook Pixel:', error); }
  }
};

export const trackFacebookEvent = (eventName: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (!isFbPixelInitialized || typeof window === 'undefined' || !window.fbq || consent.advertising !== 'granted') {
    return;
  }
  try { window.fbq('track', eventName, params); }
  catch (error) { console.error(`Marketing: Error tracking FB Pixel event ${eventName}:`, error); }
};

export const initRedditPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isRedditPixelInitialized || typeof window === 'undefined' || !window.rdt || consent.advertising !== 'granted') {
    return;
  }
  const pixelId = import.meta.env.VITE_REDDIT_PIXEL_ID;
  if (pixelId) { try { window.rdt('init', pixelId, { "optOut": false, "useDecimalCurrencyValues": true }); window.rdt('track', 'PageVisit'); isRedditPixelInitialized = true; } catch (e) { console.error('Marketing: Error init Reddit Pixel', e);}}
};

export const trackRedditEvent = (eventName: string, eventData?: object) => {
  const { consent } = useConsentStore.getState();
   if (!isRedditPixelInitialized || typeof window === 'undefined' || !window.rdt || consent.advertising !== 'granted') {
    return;
  }
  try { window.rdt('track', eventName, eventData); }
  catch (error) { console.error(`Marketing: Error tracking Reddit Pixel event ${eventName}:`, error); }
};

export const initTikTokPixel = () => {
  const { consent } = useConsentStore.getState();
  if (isTikTokPixelInitialized || typeof window === 'undefined' || !window.ttq || consent.advertising !== 'granted') {
    return;
  }
  const id = import.meta.env.VITE_TIKTOK_PIXEL_ID;
  if (id) { try { window.ttq.load(id); window.ttq.page(); isTikTokPixelInitialized = true; } catch(e){console.error('Marketing: Error init TikTok',e);}}
};

export const trackTikTokEvent = (eventName: string, params?: object) => {
  const { consent } = useConsentStore.getState();
  if (!isTikTokPixelInitialized || typeof window === 'undefined' || !window.ttq || consent.advertising !== 'granted') {
    return;
  }
  try { window.ttq.track(eventName, params); } catch(e){console.error('Marketing: Error track TikTok',e);}
};

export const initCrispChat = () => {
  const { consent } = useConsentStore.getState();
  const crispWebsiteId = import.meta.env.VITE_CRISP_WEBSITE_ID;
  if (isCrispChatInitialized || typeof window === "undefined" || !crispWebsiteId) return;

  if (consent.functional === "granted") {
    try {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = crispWebsiteId;
      const d = document;
      const s = d.createElement("script");
      s.src = "https://client.crisp.chat/l.js";
      s.async = true;
      d.getElementsByTagName("head")[0].appendChild(s);
      isCrispChatInitialized = true;
    } catch (error) {
      console.error("Marketing: Error initializing Crisp Chat:", error);
    }
  }
};

export const initializeAllTrackersBasedOnConsent = async () => {
  await getFirebaseServices(); 
  initFacebookPixel();
  initRedditPixel();
  initTikTokPixel();
  initCrispChat();
};

useConsentStore.subscribe(
  (state, prevState) => {
    const hasMadeChoiceChanged = state.hasMadeChoice && state.hasMadeChoice !== prevState.hasMadeChoice;
    const analyticsChanged = state.consent.analytics === 'granted' && prevState.consent.analytics !== 'granted';
    const advertisingChanged = state.consent.advertising === 'granted' && prevState.consent.advertising !== 'granted';
    const functionalChanged = state.consent.functional === 'granted' && prevState.consent.functional !== 'granted';

    if (hasMadeChoiceChanged || analyticsChanged || advertisingChanged || functionalChanged) {
        initializeAllTrackersBasedOnConsent();
    }
  }
);

// ===========================================================================
// ==  HIGH-VALUE ASSET TRACKING                                            ==
// ===========================================================================

export const trackResourceDownload = (resourceName: string, format: string) => {
    console.log(`[Tracking] Resource Download: ${resourceName} (${format})`);
    
    // GA4
    logFirebaseEvent('file_download', {
        file_name: resourceName,
        file_extension: format,
        link_text: `Download ${format}`
    });

    // Meta (Custom Event for High Intent)
    trackFacebookEvent('CustomEvent', { content_name: 'Resource Download', content_category: resourceName });
    
    // Reddit (ViewContent is closest standard, or custom)
    trackRedditEvent('ViewContent', { content_name: resourceName });
};

// General tool usage tracking
export const trackToolUsage = (toolName: string, action: string, metadata?: object) => {
    console.log(`[Tracking] Tool Usage: ${toolName} - ${action}`);
    
    logFirebaseEvent('use_tool', {
        tool_name: toolName,
        tool_action: action,
        ...metadata
    });

    // High intent actions (like exporting a map) deserve ad pixel tracking
    if (action === 'export' || action === 'calculate') {
         trackFacebookEvent('CustomEvent', { content_name: toolName, content_category: 'Tool Usage' });
    }
};

// Alias for backward compatibility if needed, or specific calculator logic
export const trackCalculatorUsage = (calculatorName: string, inputData: object) => {
    trackToolUsage(calculatorName, 'calculate', inputData);
};

export const trackBMPView = (bmpName: string, category: string) => {
    logFirebaseEvent('view_item', {
        item_id: bmpName,
        item_name: bmpName,
        item_category: category,
        content_type: 'bmp_guide'
    });
};

export const trackSignupIntent = (source: string, planId?: string) => {
    if (typeof window !== 'undefined') {
        sessionStorage.setItem('runofftrack.signup_intent', JSON.stringify({ source, planId: planId || null }));
    }
    logFirebaseEvent('signup_intent', {
        source,
        ...(planId ? { plan_id: planId } : {})
    });
};

export const trackExternalHandoff = (destination: string, type: 'noi_portal' | 'agency_site' | 'contact') => {
    console.log(`[Tracking] External Handoff: ${type} -> ${destination}`);

    logFirebaseEvent('external_handoff', {
        destination_url: destination,
        link_type: type
    });

    // This is a "micro-conversion" - user is taking a real world step towards compliance based on our data
    if (type === 'noi_portal') {
        trackFacebookEvent('CustomEvent', { content_name: 'External NOI Portal Click' });
    }
};
